import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Grid, Typography, IconButton } from '@mui/material';
import moment from 'moment';
import { toast } from 'react-toastify';

import {
  FHAntdDateTimePicker,
  FHAutocomplete,
  FHButton,
  FHConfirmDialog,
} from '../../components/base';
import { FORTA_BLUE, FORTA_ERROR, FORTA_SUCCESS, FORTA_WHITE } from '../../utils/colors';
import { areUserRolesIn } from '../../helpers';
import {
  Themes,
  FacilityAssignmentType,
  AssignmentStatus,
  FacilityAssignmentTypeLabel,
  Roles,
} from '../../constants';
import EditOutlinedIcon from '../../components/svgs/EditOutlinedIcon';

import type { RootState } from '../../store';
import type { Moment } from 'moment';
import type { ChangeEvent } from 'react';
import type { IClientFacilityMap, IFacility, IOption } from '../../utils/types';

const FacilityAssignmentRow = ({
  facilityAssignment,
  isAdding,
  isEditing,
  isDisabled,
  onEdit,
  onSave,
  onCancel,
  onDelete,
}: {
  facilityAssignment: IClientFacilityMap;
  isAdding: boolean;
  isEditing: boolean;
  isDisabled: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (editableFacility: IClientFacilityMap) => void;
  onDelete: (editableFacility: IClientFacilityMap) => void;
}) => {
  const { user } = useSelector((state: RootState) => state.auth);

  const { list: facilityList } = useSelector((state: RootState) => state.facilities);
  const [editableFacility, setEditableFacility] = useState({ ...facilityAssignment });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const handleFieldChange = (field: string, value: any) => {
    setEditableFacility((prevFacility: IClientFacilityMap) => ({
      ...prevFacility,
      [field]: value,
    }));
  };

  useEffect(() => {
    setEditableFacility({ ...facilityAssignment });
  }, [facilityAssignment]);

  const facilityOptions = useMemo(
    () =>
      facilityList.map((facility: IFacility) => ({
        id: facility.id,
        label: facility?.label,
      })),
    [facilityList],
  );

  const facilityTypeOptions = useMemo(
    () => [
      {
        id: FacilityAssignmentType.BILLING,
        label: FacilityAssignmentTypeLabel.BILLING,
      },
      {
        id: FacilityAssignmentType.SERVICE,
        label: FacilityAssignmentTypeLabel.SERVICE,
      },
    ],
    [],
  );
  const getAssignmentStatus = (assignment: IClientFacilityMap) => {
    const { date_start, date_end, facility } = assignment;

    if (!assignment.id || !facility) return '-';
    if (date_end && moment(date_end).isBefore(moment(), 'day')) {
      return AssignmentStatus.UNASSIGNED;
    }
    if (moment(date_start).isAfter(moment(), 'day')) {
      return AssignmentStatus.PENDING;
    }
    return AssignmentStatus.ASSIGNED;
  };

  const validate = () => {
    const { facility, date_start } = editableFacility;
    if (!facility.id) {
      toast.error('Facility is required');
      return false;
    }
    if (!editableFacility.assignment_type) {
      toast.error('Facility type is required');
      return false;
    }
    if (!date_start) {
      toast.error('Start date is required');
      return false;
    }
    return true;
  };

  const handleSave = () => {
    if (validate()) {
      onSave(editableFacility);
    }
  };

  const handleCancel = () => {
    setEditableFacility({ ...facilityAssignment });
    onCancel();
  };

  return (
    <Grid
      container
      p={2}
      alignItems="center"
      sx={{ backgroundColor: FORTA_WHITE, borderRadius: '10px', mb: 2 }}
      gap={1}
    >
      <Grid item xs={2}>
        {isAdding ? (
          <FHAutocomplete
            coloredBackground
            inputHeight={40}
            sx={{ width: '100%', boxShadow: 'none' }}
            placeholder="Select facility"
            options={facilityOptions}
            renderOption={(props: any, option: IOption) => (
              <li {...props} key={option.id}>
                {option.label}
              </li>
            )}
            value={
              facilityOptions.find(
                (option: IOption) => option.id === editableFacility.facility?.id,
              ) || ''
            }
            onChange={(event: ChangeEvent<HTMLSelectElement>, newValue: IOption | null) => {
              handleFieldChange('facility', newValue ? newValue : '');
            }}
          />
        ) : (
          <Typography textAlign={'left'}>{facilityAssignment.facility?.label}</Typography>
        )}
      </Grid>
      <Grid item xs={1.8}>
        {isAdding ? (
          <FHAutocomplete
            coloredBackground
            disableClearable
            inputHeight={40}
            sx={{ width: '100%', boxShadow: 'none' }}
            placeholder="Select facility type"
            options={facilityTypeOptions}
            renderOption={(props: any, option: IOption) => (
              <li {...props} key={option.id}>
                {option.label}
              </li>
            )}
            value={
              editableFacility.assignment_type
                ? (facilityTypeOptions.find(
                    (option: IOption) => option.id === editableFacility.assignment_type,
                  )?.label ?? '')
                : ''
            }
            onChange={(event: ChangeEvent<HTMLSelectElement>, newValue: IOption | null) => {
              handleFieldChange('assignment_type', newValue ? newValue.id : '');
            }}
          />
        ) : (
          <Typography textAlign={'center'}>
            {facilityTypeOptions.find(({ id }) => id === facilityAssignment.assignment_type)?.label}
          </Typography>
        )}
      </Grid>
      <Grid item xs={1.8}>
        {isAdding || isEditing ? (
          <FHAntdDateTimePicker
            coloredBackground
            format="MM/DD/YYYY"
            inputHeight={40}
            style={{ width: '100%', boxShadow: 'none' }}
            allowClear={false}
            value={editableFacility.date_start ? moment(editableFacility.date_start) : null}
            onSelect={(value: Moment) =>
              handleFieldChange('date_start', value.format('YYYY-MM-DD'))
            }
            onChange={(value: Moment) =>
              handleFieldChange('date_start', value.format('YYYY-MM-DD'))
            }
          />
        ) : (
          <Typography textAlign={'center'}>
            {facilityAssignment.date_start
              ? moment(facilityAssignment.date_start).format('MM/DD/YYYY')
              : 'N/A'}
          </Typography>
        )}
      </Grid>
      <Grid item xs={1.8}>
        {isAdding || isEditing ? (
          <FHAntdDateTimePicker
            coloredBackground
            format="MM/DD/YYYY"
            inputHeight={40}
            style={{ width: '100%', boxShadow: 'none' }}
            allowClear={true}
            value={editableFacility.date_end ? moment(editableFacility.date_end) : null}
            onSelect={(value: Moment) => handleFieldChange('date_end', value.format('YYYY-MM-DD'))}
            onChange={(value: Moment | null) =>
              handleFieldChange('date_end', value ? value.format('YYYY-MM-DD') : null)
            }
          />
        ) : (
          <Typography textAlign={'center'}>
            {facilityAssignment.date_end
              ? moment(facilityAssignment.date_end).format('MM/DD/YYYY')
              : 'N/A'}
          </Typography>
        )}
      </Grid>
      <Grid item xs={2}>
        <Typography
          color={
            getAssignmentStatus(editableFacility) === AssignmentStatus.ASSIGNED
              ? FORTA_SUCCESS
              : FORTA_ERROR
          }
          textAlign={'center'}
        >
          {getAssignmentStatus(editableFacility)}
        </Typography>
      </Grid>

      <Grid item xs={1}>
        {isAdding || isEditing ? (
          <FHButton disabled={isDisabled} onClick={handleSave}>
            Save
          </FHButton>
        ) : (
          areUserRolesIn(user?.roles, [Roles.Admin, Roles.OnboardingCoordinator]) && (
            <IconButton
              sx={{
                width: 54,
                height: 30,
                border: `2px solid ${FORTA_BLUE.primary}`,
                borderRadius: '20px',
                backgroundColor: isEditing ? FORTA_BLUE.primary : 'transparent',
                mr: 3,
                '&:hover': {
                  backgroundColor: isEditing ? FORTA_BLUE.primary : 'transparent',
                },
              }}
              onClick={onEdit}
            >
              <EditOutlinedIcon color={isEditing ? FORTA_WHITE : FORTA_BLUE.primary} width={18} />
            </IconButton>
          )
        )}
      </Grid>
      <Grid item xs={1}>
        {
          (isAdding || isEditing) && <FHButton onClick={handleCancel}>Cancel</FHButton>
          // : (
          //   <IconButton
          //     sx={{
          //       width: 54,
          //       height: 30,
          //       border: `2px solid ${FORTA_BLUE.primary}`,
          //       borderRadius: '20px',
          //       backgroundColor: isEditing ? FORTA_BLUE.primary : 'transparent',
          //       mr: 3,
          //       '&:hover': {
          //         backgroundColor: isEditing ? FORTA_BLUE.primary : 'transparent',
          //       },
          //     }}
          //     onClick={() => setShowConfirmDialog(true)}
          //   >
          //     <TrashIcon color={isEditing ? FORTA_WHITE : FORTA_BLUE.primary} width={18} />
          //   </IconButton>
          // )
        }
      </Grid>
      <FHConfirmDialog
        open={showConfirmDialog}
        colorTheme={Themes.Primary}
        title={'Delete client facility assignment'}
        content={`Are you sure you want to delete this client facility assignment? This action cannot be undone.`}
        submittext={'Yes, Delete'}
        canceltext={'Cancel'}
        onClose={() => setShowConfirmDialog(false)}
        onSubmit={() => {
          setShowConfirmDialog(false);
          onDelete(editableFacility);
        }}
      />
    </Grid>
  );
};

export default FacilityAssignmentRow;
